import { CloseIcon } from "@imi/icons/CloseIcon.js";
import { DataTableButton } from "@imi/ui/components/DataTable/components/DataTableButton.js";
import { useAppDispatch } from "@src/core/store";
import { createColumnHelper, TableFeatures } from "@tanstack/react-table";
import { useMemo } from "react";
import { useBatchSelectionFiltersForm } from "../../../hooks/useBatchSelectionFiltersForm";
import { deleteRow } from "../../../store/batchSelectionSlice";
import { BatchSelectionRow } from "../../../store/types";
import styles from "../BatchSelectionDataTable.module.css";
import { useBatchSelectionInputColumns } from "./useBatchSelectionInputColumns";
import { useBatchSelectionOutputColumns } from "./useBatchSelectionOutputColumns";

export const EMPTY_CELL_ID = "empty-cell";

export const columnHelper = createColumnHelper<
  TableFeatures,
  BatchSelectionRow
>();

export function useBatchSelectionDataTableColumns(
  form: ReturnType<typeof useBatchSelectionFiltersForm>["form"],
) {
  const dispatch = useAppDispatch();
  const inputsColumnsGroup = useBatchSelectionInputColumns(form);
  const outputsColumnsGroup = useBatchSelectionOutputColumns(form);

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          cell: (info) => {
            return info.row.index < info.table.getRowModel().rows.length - 1 ? (
              <div className={styles.DeleteButtonContainer}>
                <DataTableButton
                  className={styles.DeleteButton}
                  onClick={() => {
                    dispatch(deleteRow({ index: info.row.index }));
                  }}
                  variant="accent-2"
                >
                  <CloseIcon />
                </DataTableButton>
              </div>
            ) : null;
          },
          enableHiding: false,
          header: "",
          id: "delete",
          meta: {
            cell: { className: styles.DeleteButtonCell },
            draggable: false,
            header: { className: styles.DeleteButtonHeader },
          },
          size: 40,
        }),
        inputsColumnsGroup,
        columnHelper.display({
          cell: () => null,
          enableHiding: false,
          header: "",
          id: EMPTY_CELL_ID,
          meta: {
            cell: { className: styles.EmptyCell },
            draggable: false,
            header: { className: styles.EmptyHeader },
          },
          size: 55,
        }),
        outputsColumnsGroup,
      ]),
    [dispatch, inputsColumnsGroup, outputsColumnsGroup],
  );

  return { columns };
}
